<template>
  <div>
    <multiselect-search
      v-model="teamModel"
      :multiselect-class="{ 'my-multiselect': true }"
      :open-prefetch="true"
      :search-url="getSearchUrl"
      :search-options="searchOptions"
      :disabled="loading"
    >
      <template #no-options>
        {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
      </template>
      <template #no-more-infinite>
        {{ $t("dashboard.team_search_no_more", "No more teams") }}
      </template>
    </multiselect-search>
  </div>
</template>

<script setup>
import MultiselectSearch from "@/components/multiselect-search.vue";
import urls from "@/constants/urls.js";
import axios from "axios";
import { computed, getCurrentInstance, ref, watch } from "vue";
import { useTeamLeaderContacts } from "@/composables/useDashboardTeamLeaderContacts";

const vm = getCurrentInstance();
const $t = vm.proxy.$t.bind(vm.proxy);

const loading = ref(false);
const selectedTeamRef = ref(null);

const {
  campaignId: cId,
  fetchContacts,
  storeSearchQuery,
  getSearchFiltersURLParams,
  storeSearchFilters,
  teamId: storeTeamId,
  storePagination,
} = useTeamLeaderContacts();

const getSearchUrl = computed(() => {
  const orgId = localStorage.getItem("orgId");
  return urls.apiTeams.replace(":orgId", orgId).replace(":campaignId", cId.value);
});

const searchOptions = computed(() => ({
  placeholder: $t("donor_dashboard.select_team", "Select team"),
  changeParams: ({ query, page, per_page: pp }) => ({
    q: query,
    page: page,
    limit: pp,
  }),
  customLabel: (e) => `(${e.id}) ${e.name}`,
  processResults: (data) =>
    data.map((el) => ({
      id: el.id,
      ...el.attributes,
    })),
}));

const teamModel = computed({
  get: () => selectedTeamRef.value,
  set: (option) => handleTeamSelect(option),
});

async function hydrateSelectedTeam(teamId) {
  if (!teamId) {
    selectedTeamRef.value = null;
    return;
  }

  try {
    const { data } = await axios.get(getSearchUrl.value, {
      params: { team_id: teamId, limit: 1 },
    });
    const [team] = searchOptions.value.processResults(data.data);
    selectedTeamRef.value = team || null;
  } catch (e) {
    console.error(e);
    selectedTeamRef.value = null;
  }
}

async function handleTeamSelect(option) {
  selectedTeamRef.value = option;
  loading.value = true;
  const teamId = option ? option.id : null;
  storeTeamId.value = teamId;
  storePagination.value.page = 1;
  const params = getSearchFiltersURLParams(storeSearchFilters.value);

  await fetchContacts({
    campaign_id: cId,
    ...(teamId ? { team_id: teamId } : {}),
    ...(storeSearchQuery.value ? { search: storeSearchQuery.value } : {}),
    ...(params.status.length > 0 ? { status: params.status } : {}),
  });
  loading.value = false;
}

watch(
  [cId, storeTeamId],
  ([newCid, newTeamId], oldValues = []) => {
    const [oldCid] = oldValues;
    const campaignChanged = oldCid !== undefined && newCid !== oldCid;

    if (
      !campaignChanged &&
      selectedTeamRef.value &&
      Number(selectedTeamRef.value.id) === Number(newTeamId)
    ) {
      // storeTeamId just changed because this component picked it itself -- the option is
      // already known, no need to re-fetch it.
      return;
    }

    hydrateSelectedTeam(newTeamId);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
::v-deep .multiselect.my-multiselect {
  @apply tw-w-full md:tw-w-[276px] tw-rounded-full;

  .multiselect__tags {
    @apply tw-flex tw-items-center tw-min-h-[55px] md:tw-min-h-[55px] tw-ps-[24px] tw-pe-[48px] md:tw-ps-[44px] md:tw-px-[52px] tw-rounded-full tw-border-[var(--my-white-soft)] tw-bg-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__select {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%];

    &:before {
      @apply tw-border-t-[var(--my-primary)];
    }
  }

  &.multiselect--active .multiselect__select {
    @apply tw-top-[50%];
    transform: rotateZ(180deg) translateY(50%);
  }

  .multiselect__content-wrapper {
    @apply tw-rounded-[20px];
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    @apply tw-text-[18px] tw-text-[var(--my-text-color)] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-bg-[var(--my-white-soft)] tw-border-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__element .multiselect__option {
    @apply tw-block tw-truncate;
  }

  .multiselect__option--highlight {
    @apply tw-bg-[var(--my-primary)];
  }

  .multiselect__spinner {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%] tw-bg-[var(--my-white-soft)];
  }

  &.multiselect--disabled {
    .multiselect__select {
      @apply tw-bg-[var(--my-white-soft)];
    }
  }
}
</style>
